import { supabase } from '../lib/supabase';

// ========================================
// TYPES & INTERFACES
// ========================================

export interface ExpenseCategory {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  category_type: 'general' | 'warranty' | 'both';
  is_active: boolean;
  display_order: number;
  default_xero_account_code?: string | null;
  default_xero_tax_type?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface VehicleExpense {
  id: string;
  organization_id: string;
  vehicle_id: string;
  cost_type: string; // e.g., 'general_detailing', 'warranty_repair'
  expense_category_id?: string;
  expense_category?: ExpenseCategory; // Populated via join
  expense_date: string;
  provider?: string; // Supplier name (free text)
  paid_to_customer_id?: string; // Link to customers table
  description?: string;
  reference_number?: string;
  supplier_invoice_number?: string;
  amount: number;
  gst_amount: number;
  has_tax_invoice: boolean;
  non_accounting: boolean;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  date_paid?: string;
  payment_method?: string;
  bank_account?: string;
  purchase_order_number?: string;
  purchase_order_id?: string;
  purchase_order?: { id: string; po_number: string; status: string; due_date: string | null };
  xero_account_code?: string;
  xero_tax_type?: string;
  documents?: any[];
  notes?: string;
  valid_from?: string;
  valid_to?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
}

export interface IncentiveBonus {
  id: string;
  organization_id: string;
  vehicle_id: string;
  bonus_type: string;
  distributor_name?: string;
  description?: string;
  amount: number;
  currency: string;
  status: 'expected' | 'received' | 'cancelled';
  expected_date?: string;
  received_date?: string;
  reference_number?: string;
  documents?: any[];
  notes?: string;
  // Xero — bonuses post to a Revenue account (ACCREC), not an expense account
  xero_account_code?: string | null;
  xero_tax_type?: string | null;
  xero_invoice_id?: string | null;
  xero_sync_status?: 'pending' | 'pushed' | 'error' | null;
  xero_synced_at?: string | null;
  xero_sync_error?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface DefaultExpense {
  id: string;
  organization_id: string;
  name: string;
  description?: string;
  expense_category_id?: string;
  expense_category?: ExpenseCategory;
  default_amount: number;
  default_gst_amount: number;
  apply_to_all: boolean;
  apply_to_condition?: string;
  apply_to_acquisition_method?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface CreateExpenseInput {
  vehicle_id: string;
  cost_type: string; // e.g., 'general_detailing', 'warranty_repair'
  expense_category_id?: string;
  expense_date: string;
  provider?: string; // Supplier name (free text)
  paid_to_customer_id?: string;
  description?: string;
  reference_number?: string;
  supplier_invoice_number?: string;
  amount: number;
  gst_amount?: number;
  has_tax_invoice?: boolean;
  non_accounting?: boolean;
  status?: 'pending' | 'paid' | 'overdue' | 'cancelled';
  date_paid?: string;
  payment_method?: string;
  bank_account?: string;
  notes?: string;
}

export interface CreateBonusInput {
  vehicle_id: string;
  bonus_type: string;
  distributor_name?: string;
  description?: string;
  amount: number;
  status?: 'expected' | 'received' | 'cancelled';
  expected_date?: string;
  received_date?: string;
  reference_number?: string;
  notes?: string;
}

export interface CreateCategoryInput {
  name: string;
  description?: string;
  category_type: 'general' | 'warranty' | 'both';
  display_order?: number;
  is_active?: boolean;
  default_xero_account_code?: string | null;
  default_xero_tax_type?: string | null;
}

// ========================================
// EXPENSE SERVICE CLASS
// ========================================

class ExpenseService {
  // ========================================
  // EXPENSE CATEGORIES
  // ========================================

  async getCategories(type?: 'general' | 'warranty' | 'both'): Promise<ExpenseCategory[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!userData?.organization_id) throw new Error('No organization found');

      let query = supabase
        .from('expense_categories')
        .select('*')
        .eq('organization_id', userData.organization_id)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (type) {
        query = query.or(`category_type.eq.${type},category_type.eq.both`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    } catch (error: any) {
      console.error('Error fetching expense categories:', error);
      throw error;
    }
  }

  async createCategory(input: CreateCategoryInput): Promise<ExpenseCategory> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!userData?.organization_id) throw new Error('No organization found');

      const { data, error } = await supabase
        .from('expense_categories')
        .insert({
          organization_id: userData.organization_id,
          created_by: user.id,
          ...input
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error creating expense category:', error);
      throw error;
    }
  }

  async updateCategory(id: string, updates: Partial<ExpenseCategory>): Promise<ExpenseCategory> {
    try {
      const { data, error } = await supabase
        .from('expense_categories')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error updating expense category:', error);
      throw error;
    }
  }

  async deleteCategory(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('expense_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error: any) {
      console.error('Error deleting expense category:', error);
      throw error;
    }
  }

  // ========================================
  // VEHICLE EXPENSES
  // ========================================

  async getExpensesByVehicle(vehicleId: string, type?: 'general' | 'warranty'): Promise<VehicleExpense[]> {
    try {
      let query = supabase
        .from('vehicle_costs')
        .select(`
          *,
          expense_category:expense_categories(*),
          purchase_order:purchase_orders(id, po_number, status, due_date)
        `)
        .eq('vehicle_id', vehicleId)
        .order('expense_date', { ascending: false });

      if (type) {
        // Match both legacy sub-typed values (e.g. 'general_repair') and the
        // new collapsed values ('general' / 'warranty').
        query = query.or(`cost_type.like.${type}_%,cost_type.eq.${type}`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    } catch (error: any) {
      console.error('Error fetching vehicle expenses:', error);
      throw error;
    }
  }

  async createExpense(input: CreateExpenseInput): Promise<VehicleExpense> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!userData?.organization_id) throw new Error('No organization found');

      const { data, error } = await supabase
        .from('vehicle_costs')
        .insert({
          organization_id: userData.organization_id,
          created_by: user.id,
          ...input,
          gst_amount: input.gst_amount || 0,
          has_tax_invoice: input.has_tax_invoice ?? false,
          non_accounting: input.non_accounting ?? false,
          status: input.status || 'pending'
        })
        .select(`
          *,
          expense_category:expense_categories(*)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error creating expense:', error);
      throw error;
    }
  }

  async updateExpense(id: string, updates: Partial<VehicleExpense>): Promise<VehicleExpense> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('vehicle_costs')
        .update({
          ...updates,
          updated_by: user.id,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select(`
          *,
          expense_category:expense_categories(*)
        `)
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error updating expense:', error);
      throw error;
    }
  }

  async deleteExpense(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('vehicle_costs')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error: any) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  }

  async markExpenseAsPaid(id: string, paymentDetails: {
    date_paid: string;
    payment_method?: string;
    bank_account?: string;
  }): Promise<VehicleExpense> {
    return this.updateExpense(id, {
      status: 'paid',
      ...paymentDetails
    });
  }

  // ========================================
  // INCENTIVE BONUSES
  // ========================================

  async getBonusesByVehicle(vehicleId: string): Promise<IncentiveBonus[]> {
    try {
      const { data, error } = await supabase
        .from('incentive_bonuses')
        .select('*')
        .eq('vehicle_id', vehicleId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching incentive bonuses:', error);
      throw error;
    }
  }

  async createBonus(input: CreateBonusInput): Promise<IncentiveBonus> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!userData?.organization_id) throw new Error('No organization found');

      const { data, error } = await supabase
        .from('incentive_bonuses')
        .insert({
          organization_id: userData.organization_id,
          created_by: user.id,
          currency: 'AUD',
          status: input.status || 'expected',
          ...input
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error creating incentive bonus:', error);
      throw error;
    }
  }

  async updateBonus(id: string, updates: Partial<IncentiveBonus>): Promise<IncentiveBonus> {
    try {
      const { data, error } = await supabase
        .from('incentive_bonuses')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error: any) {
      console.error('Error updating incentive bonus:', error);
      throw error;
    }
  }

  async deleteBonus(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('incentive_bonuses')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error: any) {
      console.error('Error deleting incentive bonus:', error);
      throw error;
    }
  }

  async markBonusAsReceived(id: string, received_date: string): Promise<IncentiveBonus> {
    return this.updateBonus(id, {
      status: 'received',
      received_date
    });
  }

  // ========================================
  // DEFAULT EXPENSES
  // ========================================

  async getDefaultExpenses(): Promise<DefaultExpense[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: userData } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user.id)
        .single();

      if (!userData?.organization_id) throw new Error('No organization found');

      const { data, error } = await supabase
        .from('default_expenses')
        .select(`
          *,
          expense_category:expense_categories(*)
        `)
        .eq('organization_id', userData.organization_id)
        .eq('is_active', true);

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Error fetching default expenses:', error);
      throw error;
    }
  }

  async applyDefaultExpenses(vehicleId: string, condition?: string, acquisitionMethod?: string): Promise<VehicleExpense[]> {
    try {
      const defaults = await this.getDefaultExpenses();
      const applicable = defaults.filter(def => {
        if (def.apply_to_all) return true;
        if (def.apply_to_condition && def.apply_to_condition !== condition) return false;
        if (def.apply_to_acquisition_method && def.apply_to_acquisition_method !== acquisitionMethod) return false;
        return true;
      });

      const createdExpenses: VehicleExpense[] = [];
      for (const defaultExp of applicable) {
        const expense = await this.createExpense({
          vehicle_id: vehicleId,
          cost_type: 'general_default',
          expense_category_id: defaultExp.expense_category_id,
          expense_date: new Date().toISOString().split('T')[0],
          description: defaultExp.description || defaultExp.name,
          amount: defaultExp.default_amount,
          gst_amount: defaultExp.default_gst_amount,
          status: 'pending'
        });
        createdExpenses.push(expense);
      }

      return createdExpenses;
    } catch (error: any) {
      console.error('Error applying default expenses:', error);
      throw error;
    }
  }

  // ========================================
  // CALCULATIONS & REPORTING
  // ========================================

  async getTotalExpensesByVehicle(vehicleId: string): Promise<{
    generalExpenses: number;
    warrantyExpenses: number;
    totalExpenses: number;
    paidExpenses: number;
    pendingExpenses: number;
  }> {
    try {
      const expenses = await this.getExpensesByVehicle(vehicleId);

      const general = expenses
        .filter(e => e.cost_type?.startsWith('general_'))
        .reduce((sum, e) => sum + e.amount + (e.gst_amount || 0), 0);

      const warranty = expenses
        .filter(e => e.cost_type?.startsWith('warranty_'))
        .reduce((sum, e) => sum + e.amount + (e.gst_amount || 0), 0);

      const paid = expenses
        .filter(e => e.status === 'paid')
        .reduce((sum, e) => sum + e.amount + (e.gst_amount || 0), 0);

      const pending = expenses
        .filter(e => e.status === 'pending')
        .reduce((sum, e) => sum + e.amount + (e.gst_amount || 0), 0);

      return {
        generalExpenses: general,
        warrantyExpenses: warranty,
        totalExpenses: general + warranty,
        paidExpenses: paid,
        pendingExpenses: pending
      };
    } catch (error: any) {
      console.error('Error calculating expense totals:', error);
      throw error;
    }
  }

  async getTotalBonusesByVehicle(vehicleId: string): Promise<{
    expectedBonuses: number;
    receivedBonuses: number;
    totalBonuses: number;
  }> {
    try {
      const bonuses = await this.getBonusesByVehicle(vehicleId);

      const expected = bonuses
        .filter(b => b.status === 'expected')
        .reduce((sum, b) => sum + b.amount, 0);

      const received = bonuses
        .filter(b => b.status === 'received')
        .reduce((sum, b) => sum + b.amount, 0);

      return {
        expectedBonuses: expected,
        receivedBonuses: received,
        totalBonuses: expected + received
      };
    } catch (error: any) {
      console.error('Error calculating bonus totals:', error);
      throw error;
    }
  }

  /**
   * Push a bonus incentive to Xero as ACCREC (sales invoice).
   *
   * Bonuses are INCOME — distributor owes us money — so they post as Accounts
   * Receivable, not Accounts Payable. The Contact is the distributor; the line
   * goes to a Revenue account (e.g. 260 Other Revenue).
   *
   * Different code path from purchaseOrderService.pushPurchaseOrderToXero
   * because the entity, direction, and account category are all different.
   */
  async pushBonusToXero(
    bonusId: string,
    userId: string
  ): Promise<{ success: boolean; xeroInvoiceId?: string; error?: string }> {
    const { XeroIntegrationService } = await import('./xero-integration.service');

    const { data: bonus, error } = await supabase
      .from('incentive_bonuses')
      .select('*')
      .eq('id', bonusId)
      .single();
    if (error || !bonus) return { success: false, error: 'Bonus not found' };
    if ((bonus as any).xero_invoice_id) {
      return { success: false, error: `Already pushed to Xero (${(bonus as any).xero_invoice_id})` };
    }
    if (bonus.status === 'cancelled') {
      return { success: false, error: 'Cancelled bonuses cannot be pushed' };
    }
    if (!(bonus as any).xero_account_code) {
      return { success: false, error: 'Set a Revenue Xero account on this bonus first' };
    }

    const invoicePayload = {
      Type: 'ACCREC', // Sales invoice — money owed TO us
      Contact: { Name: bonus.distributor_name || 'Bonus Distributor' },
      Reference: bonus.reference_number || `BONUS-${bonus.id.slice(0, 8)}`,
      Date: bonus.received_date || bonus.expected_date || new Date().toISOString().slice(0, 10),
      DueDate: bonus.expected_date || undefined,
      LineAmountTypes: 'Exclusive',
      Status: 'AUTHORISED',
      LineItems: [
        {
          Description: bonus.description || `${bonus.bonus_type} bonus`,
          Quantity: 1,
          UnitAmount: bonus.amount,
          AccountCode: (bonus as any).xero_account_code,
          TaxType: (bonus as any).xero_tax_type || 'BASEXCLUDED',
        },
      ],
    };

    await supabase
      .from('incentive_bonuses')
      .update({ xero_sync_status: 'pending', xero_sync_error: null, updated_at: new Date().toISOString() })
      .eq('id', bonusId);

    let response: Response | null = null;
    try {
      response = await XeroIntegrationService.makeAuthenticatedRequest(userId, '/Invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ Invoices: [invoicePayload] }),
      });
    } catch (e: any) {
      await supabase
        .from('incentive_bonuses')
        .update({ xero_sync_status: 'error', xero_sync_error: e?.message || String(e) })
        .eq('id', bonusId);
      return { success: false, error: e?.message || 'Xero request failed' };
    }

    if (!response || !response.ok) {
      const text = response ? await response.text() : 'No response from Xero';
      await supabase
        .from('incentive_bonuses')
        .update({ xero_sync_status: 'error', xero_sync_error: text.slice(0, 500) })
        .eq('id', bonusId);
      return { success: false, error: text.slice(0, 200) };
    }

    const json = await response.json();
    const created = json?.Invoices?.[0];
    if (!created?.InvoiceID) {
      await supabase
        .from('incentive_bonuses')
        .update({ xero_sync_status: 'error', xero_sync_error: 'Xero returned no InvoiceID' })
        .eq('id', bonusId);
      return { success: false, error: 'Xero returned no InvoiceID' };
    }

    await supabase
      .from('incentive_bonuses')
      .update({
        xero_invoice_id: created.InvoiceID,
        xero_sync_status: 'pushed',
        xero_synced_at: new Date().toISOString(),
        xero_sync_error: null,
      })
      .eq('id', bonusId);

    await supabase.from('xero_sync_log').insert({
      organization_id: bonus.organization_id,
      resource_type: 'incentive_bonus',
      xero_id: created.InvoiceID,
      sync_status: 'success',
      data: { bonus_id: bonusId, invoice_id: created.InvoiceID, type: 'ACCREC' },
      last_synced: new Date().toISOString(),
    });

    return { success: true, xeroInvoiceId: created.InvoiceID };
  }
}

export const expenseService = new ExpenseService();
